/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { StyledListItem, StyledOverViewLabel, StyledOverViewLink } from './NavigationStyles';
import { TrackingServiceContext, useInPageDispatch, useInPageNavContext } from '../../context';
import { getParentItemHref, getParentItemTitle, getTrackingLabelDisplay } from '../../utils/utils';
import { ActiveLinks } from '../../../types';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';
import { getIndexPosition, TrackingRelatedElements, useTracking } from '../../hooks/use-tracking';
import { topLevelRelatedSingleElement, trackClickEvent } from '../../utils/tracking-utils';

interface OverViewLabelProps {
  isSecondaryLevel?: boolean;
}

interface LabelProps extends OverViewLabelProps {
  isParentItem?: boolean;
}

const Link = React.memo(() => {
  const dispatch = useInPageDispatch();
  const {
    parentPageInpageNavigation,
    sameLevelInpageNavigation,
    isLoadedFromUrl,
    subNavActive,
    impressionTracking,
  } = useInPageNavContext();
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);
  const propertyPath = 'parentPageInpageNavigation';
  if (!getParentItemHref(parentPageInpageNavigation)) {
    return <SecondLevelLabel isParentItem />;
  }

  const handleSelectedItem = useCallback(
    ({ id, index, text, href, target }: ActiveLinks) => {
      dispatch?.({
        type: INPAGENAV_ACTIONS.SAME_LEVEL_ACTIVE_LINK,
        payload: {
          sameLevelActiveLink: { id, index, text, href, target, overviewLinkSelected: true },
        },
      });
    },
    [dispatch, tracking, subNavActive],
  );

  const relatedElements = topLevelRelatedSingleElement(0, sameLevelInpageNavigation);

  const title = getParentItemTitle(parentPageInpageNavigation);

  const path = getParentItemHref(parentPageInpageNavigation) || '';

  return (
    <StyledOverViewLink
      aria-label={title}
      className={!isLoadedFromUrl ? 'active-link' : ''}
      href={path}
      onClick={() => {
        const { id = '', fields } = parentPageInpageNavigation || {};
        const { subLevelParentLabel = '' } = fields || {};

        handleSelectedItem({
          id,
          index: 0,
          text: subLevelParentLabel,
          href: path,
          target: '_self',
        });

        if (typeof window !== 'undefined' && path && isLoadedFromUrl) {
          // tracking for topnav parent link click
          trackClickEvent(
            tracking,
            subLevelParentLabel,
            impressionTracking?.value || '',
            path,
            getIndexPosition(0).toString(),
            relatedElements,
          );
        }
      }}
      rel="noopener"
      target="_self"
      title={title}
      $hasParentElement
      $isSecondaryLevel={false}
    >
      <UeContainer propertyPath={propertyPath} label="Navigation item">
        <UeElement type="text" property="subLevelParentLabel" label="Parent Page Item Label">
          {getParentItemTitle(parentPageInpageNavigation)}
        </UeElement>
      </UeContainer>
    </StyledOverViewLink>
  );
});

const SecondLevelLabel = React.memo(({ isParentItem, isSecondaryLevel }: LabelProps) => {
  const dispatch = useInPageDispatch();
  const {
    parentPageInpageNavigation,
    sameLevelActiveLink,
    topLevelActiveLink,
    secondLevelActiveLink,
    isLoadedFromUrl,
    impressionTracking,
  } = useInPageNavContext();
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);
  const [activeLink, setActivelink] = useState<ActiveLinks>();
  const [label, setLabel] = useState<string>('');

  useEffect(() => {
    if (!isParentItem) {
      setActivelink(isSecondaryLevel ? topLevelActiveLink : secondLevelActiveLink);
    }
  }, [isParentItem]);

  useEffect(() => {
    const overviewLabel =
      activeLink?.parentLabel ||
      activeLink?.subLevelChildLabel ||
      activeLink?.text ||
      topLevelActiveLink?.text ||
      topLevelActiveLink?.subLevelChildLabel ||
      parentPageInpageNavigation?.fields?.subLevelParentLabel ||
      '';

    setLabel(overviewLabel);
  }, [activeLink, topLevelActiveLink, parentPageInpageNavigation]);

  const handleSelectedItem = useCallback(
    ({ id, index, text, href, target }: ActiveLinks) => {
      dispatch?.({
        type: INPAGENAV_ACTIONS.SUB_LEVEL_ACTIVE_LINK,
        payload: {
          secondLevelActiveLink: { id, index, text, href, target, overviewLinkSelected: true },
        },
      });
    },
    [dispatch],
  );

  const propertyPath = isSecondaryLevel
    ? `sameLevelInpageNavigation.${activeLink?.index}`
    : 'parentPageInpageNavigation';

  return (sameLevelActiveLink?.subLevelChildLabel || sameLevelActiveLink?.text) &&
    sameLevelActiveLink?.href &&
    !topLevelActiveLink &&
    !isParentItem ? (
    <StyledOverViewLink
      aria-label={sameLevelActiveLink.subLevelChildLabel}
      href={sameLevelActiveLink?.href}
      className="active-link"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        const { id, index, text, href, target } = sameLevelActiveLink || {};

        handleSelectedItem({ id, index, text, href, target });
      }}
      rel="noopener"
      target={sameLevelActiveLink?.target}
      title={sameLevelActiveLink.subLevelChildLabel}
      $isSecondaryLevel
      $subPageParent
      $isActive
    >
      <UeContainer propertyPath={propertyPath} label="Navigation Item">
        <UeElement type="text" property="Text" label="Parent Page Item Link">
          {sameLevelActiveLink.subLevelChildLabel || sameLevelActiveLink.text}
        </UeElement>
      </UeContainer>
    </StyledOverViewLink>
  ) : topLevelActiveLink?.href && !isParentItem ? (
    <StyledOverViewLink
      aria-label={topLevelActiveLink.subLevelChildLabel || topLevelActiveLink.text}
      href={topLevelActiveLink?.href}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSelectedItem(topLevelActiveLink);
        if (typeof window !== 'undefined' && topLevelActiveLink?.href) {
          const { target, href } = topLevelActiveLink;

          const index = topLevelActiveLink?.index as number;
          const relatedElements: TrackingRelatedElements[] = [
            {
              elementName: 'navigation item',
              elementValue: topLevelActiveLink?.subLevelChildLabel || '',
            },
          ];

          const trackingIndex = (topLevelActiveLink?.index as number) - 1;

          const trackinglabel = `${getTrackingLabelDisplay([`${topLevelActiveLink?.text}`, topLevelActiveLink?.subLevelChildLabel || ''])}`;

          // tracking for subnav parent link click
          trackClickEvent(
            tracking,
            trackinglabel,
            impressionTracking?.value || '',
            href || '',
            `${getIndexPosition(index, true).toString()}-${getIndexPosition(trackingIndex).toString()}`,
            relatedElements,
          );

          target === '_blank' ? window.open(href) : (window.location.href = href);
        }
      }}
      rel="noopener"
      target={topLevelActiveLink?.target}
      title={topLevelActiveLink.subLevelChildLabel || topLevelActiveLink.text}
      $isSecondaryLevel
    >
      <UeContainer propertyPath={propertyPath} label="Navigation Item">
        <UeElement type="text" property="subLevelChildLabel" label="Sub Level Child Label">
          {topLevelActiveLink.subLevelChildLabel || topLevelActiveLink.text}
        </UeElement>
      </UeContainer>
    </StyledOverViewLink>
  ) : (
    <StyledOverViewLabel
      aria-describedby="id"
      aria-label={label}
      className={`${!isLoadedFromUrl && isParentItem ? 'active-link' : ''}`}
      $hasParentElement
      $isSecondaryLevel={isSecondaryLevel}
    >
      <UeContainer propertyPath={propertyPath} label="Navigation item">
        <UeReference propertyPath={propertyPath} label="Label field">
          <UeElement type="text" property="Text" label="Parent Page Item Label">
            {label}
          </UeElement>
        </UeReference>
      </UeContainer>
    </StyledOverViewLabel>
  );
});

const OverViewLabel: React.FC<OverViewLabelProps> = ({ isSecondaryLevel }) => {
  return (
    <StyledListItem
      role="none"
      className="parent-item"
      $isSecondaryLevel={isSecondaryLevel}
      $hasParentElement
      $isMobile
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      {isSecondaryLevel ? <SecondLevelLabel isSecondaryLevel={isSecondaryLevel} /> : <Link />}
    </StyledListItem>
  );
};

export default OverViewLabel;
